import React, { useEffect, useState, useMemo, Suspense, lazy } from 'react';
import { Routes, Route, BrowserRouter } from 'react-router-dom';
import { createTheme } from '@mui/material/styles';
import { ThemeProvider } from '@material-ui/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ColorModeContext from './config/color-context';
import Loading from './components/loading/Loading';
import isMobileDevice from './utils/isMobileDevice';
import NotificationProvider from './components/notifications/NotificationProvider';
import './index.css';

// Lazy load all route components with preloading
const Home = lazy(() => import('./views/home'));
const Microsite = lazy(() => import('./views/microsite'));
const ShortUrl = lazy(() => import('./views/shortUrl'));
const BusinessCardPage = lazy(() => import('./views/businessCard'));
const BusinessCardDetailsPage = lazy(() =>
  import('./views/businessCardDetails'),
);

// Preload critical routes
const preloadCriticalRoutes = () => {
  // Preload home page immediately after initial load
  setTimeout(() => {
    import('./views/home');
  }, 1000);
};

const palette = {
  default: {
    light: '#FFFFFF',
    dark: '#020427',
    cardDark: '#353652',
    black: '#333333',
  },
};

const getDesignTokens = (mode) => ({
  breakpoints: {
    values: {
      sm: 540, // Small devices (larger phones)
      md: 768, // Medium devices (tablets)
      lg: 1024, // Large devices (desktops)
      xl: 1280, // Extra large devices (large desktops)
      '2xl': 1536, // Extra extra large devices (widescreens)
    },
  },
  palette: {
    mode,
    ...(mode === 'light'
      ? {
          // palette values for light mode
          background: {
            default: palette.default.light,
          },
          text: {
            default: palette.default.black,
          },
        }
      : {
          // palette values for dark mode
          background: {
            default: palette.default.dark,
            cardDefault: palette.default.cardDark,
          },
          text: {
            default: palette.default.light,
          },
        }),
  },
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
    },
  },
});

const AppRoutes = () => (
  <Suspense fallback={<Loading />}>
    <Routes>
      <Route path="/" element={<Home />} />
      <Route path="/:micrositeId" element={<Microsite />} />
      <Route path="/s/:slug" element={<ShortUrl />} />
      <Route
        path="/business_cards/:businessCardId"
        element={<BusinessCardPage />}
      />
      <Route
        path="/business_cards/:businessCardId/details"
        element={<BusinessCardDetailsPage />}
      />
    </Routes>
  </Suspense>
);

const App = () => {
  const [mode, setMode] = useState(isMobileDevice() ? 'light' : 'dark');

  const colorMode = useMemo(
    () => ({
      // The dark mode switch would invoke this method
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === 'light' ? 'dark' : 'light'));
      },
    }),
    [],
  );

  // Update the theme only if the mode changes
  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  useEffect(() => {
    document.body.style.backgroundColor = theme.palette.background.default;
    // Preload critical routes after initial render
    preloadCriticalRoutes();
  }, [theme]);

  return (
    <ColorModeContext.Provider value={colorMode}>
      <ThemeProvider theme={theme}>
        <QueryClientProvider client={queryClient}>
          <NotificationProvider>
            <div className="App">
              <BrowserRouter>
                <AppRoutes />
              </BrowserRouter>
            </div>
          </NotificationProvider>
        </QueryClientProvider>
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default App;
